import { nextTick, ref } from 'vue';
import { createI18n } from 'vue-i18n';
import defaultMessages from '@/locales/en.json';
import type { Localization } from '@/types';

export const supportedLocales: Record<string, { name: string }> = {
	en: { name: 'English' },
	de: { name: 'German' },
	es: { name: 'Spanish' },
	fr: { name: 'French' },
	it: { name: 'Italian' },
	nl: { name: 'Dutch' },
	pt: { name: 'Portuguese' },
	pl: { name: 'Polish' },
	ar: { name: 'Arabic' },
	cs: { name: 'Czech' },
	da: { name: 'Danish' },
	fi: { name: 'Finnish' },
	id: { name: 'Indonesian' },
	ja: { name: 'Japanese' },
	ko: { name: 'Korean' },
	ms: { name: 'Malay' },
	no: { name: 'Norwegian' },
	ro: { name: 'Romanian' },
	sv: { name: 'Swedish' },
	th: { name: 'Thai' },
	tr: { name: 'Turkish' },
	vi: { name: 'Vietnamese' },
	zh: { name: 'Chinese' },
	fil: { name: 'Filipino' },
};

export const defaultLocale = 'en';

const _i18n = ref();

function setup(options = { locale: defaultLocale }) {
	_i18n.value = createI18n({
		legacy: false,
		locale: options.locale,
		fallbackLocale: defaultLocale,
		messages: { [defaultLocale]: defaultMessages },
	});
	setLocale(options.locale);
}

export function setLocale(newLocale: string): void {
	_i18n.value.global.locale = newLocale;
	setDocumentAttributesFor(newLocale);
}

export async function loadMessagesFor(locale: string): Promise<void> {
	const { localizations } = await import('@/constants/localizations');
	const localization = localizations.find(
		(localizationItem: Localization) => localizationItem.code === locale
	);
	const fileName = localization?.file || 'en';

	const messages = await import(
		/* webpackChunkName: "locale-[request]" */ `./locales/${fileName}.json`
	);

	_i18n.value.global.setLocaleMessage(locale, messages.default);

	return nextTick();
}

function setDocumentAttributesFor(locale: string): void {
	const htmlElement = document.querySelector('html');
	htmlElement?.setAttribute('lang', locale);
}

export default {
	get vueI18n() {
		return _i18n.value;
	},
	setup,
};
